import type { CopyStuffsInput, CopyStuffsResult, LearnStatus, MapCount, Scope, Stuff, StuffClip, StuffInput, StuffMedia, MediaCaption } from "@69ly/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiFailure } from "./api.ts";
import { shrinkImage } from "./shrink-image.ts";

export const stuffKeys = {
  all: ["stuffs"] as const,
  list: (scope: Scope, map: string) => ["stuffs", "list", scope, map] as const,
  one: (id: string) => ["stuffs", "one", id] as const,
  counts: (scope: Scope) => ["stuffs", "counts", scope] as const,
};

export const useStuffs = (scope: Scope, map: string) =>
  useQuery({
    queryKey: stuffKeys.list(scope, map),
    queryFn: () => api<Stuff[]>("GET", `/stuffs?scope=${encodeURIComponent(scope)}&map=${encodeURIComponent(map)}`),
  });

export const useStuff = (id: string | undefined) =>
  useQuery({ queryKey: stuffKeys.one(id ?? ""), queryFn: () => api<Stuff>("GET", `/stuffs/${id}`), enabled: Boolean(id) });

export const useMapCounts = (scope: Scope) =>
  useQuery({ queryKey: stuffKeys.counts(scope), queryFn: () => api<MapCount[]>("GET", `/stuffs/counts?scope=${encodeURIComponent(scope)}`) });

export function useSaveStuff(id: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: StuffInput & { scope?: Scope }) =>
      id ? api<Stuff>("PUT", `/stuffs/${id}`, input) : api<Stuff>("POST", "/stuffs", input),
    onSuccess: (stuff) => {
      qc.setQueryData(stuffKeys.one(stuff.id), stuff);
      return qc.invalidateQueries({ queryKey: stuffKeys.all });
    },
  });
}

export function useDeleteStuff() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>("DELETE", `/stuffs/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: stuffKeys.all }),
  });
}

export function useSetStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: LearnStatus | null }) =>
      api<{ status: LearnStatus | null }>("PUT", `/stuffs/${id}/status`, { status }),
    onSuccess: () => qc.invalidateQueries({ queryKey: stuffKeys.all }),
  });
}

/** Uploads one picture, made smaller first if big. Uses fetch directly: the body is a form, not JSON. */
export async function uploadPicture(stuffId: string, original: File, caption: MediaCaption): Promise<StuffMedia> {
  const file = await shrinkImage(original);
  const form = new FormData();
  form.append("file", file);
  const res = await fetch(`/api/stuffs/${stuffId}/media?caption=${caption}`, { method: "POST", body: form, credentials: "same-origin" });
  const data = (await res.json().catch(() => null)) as { code?: ApiFailure["code"]; message?: string } | null;
  // A proxy in front of the API (ingress body limit) answers 413 in HTML, without our JSON.
  if (res.status === 413 && !data?.code) throw new ApiFailure(413, "too_large", "Too large");
  if (!res.ok) throw new ApiFailure(res.status, data?.code ?? "internal", data?.message ?? res.statusText);
  return data as unknown as StuffMedia;
}

export function useRemovePicture(stuffId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (mediaId: string) => api<void>("DELETE", `/stuffs/${stuffId}/media/${mediaId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: stuffKeys.all }),
  });
}

/**
 * Uploads the stuff's video, reporting progress (0 to 1): videos are big, the
 * player should see it move. XMLHttpRequest, because fetch cannot report upload progress.
 */
export function uploadClip(stuffId: string, file: File, onProgress: (done: number) => void, url = `/api/stuffs/${stuffId}/clip`): Promise<StuffClip> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url);
    xhr.withCredentials = true;
    xhr.responseType = "json";
    xhr.upload.onprogress = (e) => e.lengthComputable && onProgress(e.loaded / e.total);
    xhr.onerror = () => reject(new ApiFailure(0, "network", "Network error"));
    xhr.onload = () => {
      const data = xhr.response as { code?: ApiFailure["code"]; message?: string } | null;
      if (xhr.status >= 200 && xhr.status < 300) return resolve(data as unknown as StuffClip);
      if (xhr.status === 413 && !data?.code) return reject(new ApiFailure(413, "too_large", "Too large"));
      reject(new ApiFailure(xhr.status, data?.code ?? "internal", data?.message ?? xhr.statusText));
    };
    const form = new FormData();
    form.append("file", file);
    xhr.send(form);
  });
}

export function useRemoveClip(stuffId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api<void>("DELETE", `/stuffs/${stuffId}/clip`),
    onSuccess: () => qc.invalidateQueries({ queryKey: stuffKeys.all }),
  });
}

/** Copies stuffs into another library (see POST /stuffs/copy). */
export function useCopyStuffs() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CopyStuffsInput) => api<CopyStuffsResult>("POST", "/stuffs/copy", input),
    onSuccess: () => qc.invalidateQueries({ queryKey: stuffKeys.all }),
  });
}
